import i18n from '@/plugins/i18next'
import { formatPhone, getCallingCode } from '@/plugins/phonenumber-helper'
import moment from 'moment'
import VueCookie from 'vue-cookie'
import { JSEncrypt } from 'jsencrypt'
import * as constants from '../mixins/constants'
import { generateRequestId, encodeRequestId } from './requestId'

const { find, cloneDeep, get, isObject, isArray, pick, filter } = require('@/plugins/lodash')
// import { googleMapStyles } from "./google-map-styles";

/**
 * Wrapper for localStorage
 */
function storageFactory() {
  let inMemoryStorage = {}
  let storage

  function isSupported() {
    try {
      const key = 'storage'
      storage = localStorage
      storage.setItem(key, key)
      storage.removeItem(key)
      return true
    } catch (e) {
      return false
    }
  }

  function getItem(key) {
    if (isSupported()) {
      return storage.getItem(key)
    }
    return inMemoryStorage[key] || null
  }

  function setItem(key, value) {
    if (isSupported()) {
      storage.setItem(key, value)
    } else {
      inMemoryStorage[key] = value
    }
  }

  function removeItem(key) {
    if (isSupported()) {
      storage.removeItem(key)
    } else {
      delete inMemoryStorage[key]
    }
  }

  function clear() {
    if (isSupported()) {
      storage.clear()
    } else {
      inMemoryStorage = {}
    }
  }

  function key(n) {
    if (isSupported()) {
      return storage.key(n)
    } else {
      return Object.keys(inMemoryStorage)[n] || null
    }
  }

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    key
  }
}

const localStorageWrapper = storageFactory()
export const getItemStorage = function (key) {
  return localStorageWrapper.getItem(key)
}

export const setItemStorage = function (key, data) {
  localStorageWrapper.setItem(key, data)
}

export const removeItemStorage = function (key) {
  localStorageWrapper.removeItem(key)
}

export const updateItemStorage = function (key, newProps) {
  const oldVal = getItemStorage(key)
  if (oldVal) {
    const newVal = Object.assign({}, JSON.parse(oldVal), newProps)
    setItemStorage(key, JSON.stringify(newVal))
  }
}

/* End localStorage */

export const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const preHTTPS = function (url) {
  let result = url ? ''.concat(url) : url
  if (result && result.indexOf('http://') === 0) {
    result = result.replace('http://', 'https://')
  }
  return result
}

// TODO: init app version
export const initAppVersion = function ({ $store, $cookie, $constants }) {
  if ($store.state.version1) return $store.state.version1
  const appVersion1 = $cookie.get($constants.COOKIE_NAME__APP_VERSION_1)
  if (appVersion1) {
    const val = appVersion1 === 'true'
    $store.dispatch('setAppVersion1', val)
    return appVersion1
  }
  return null
}

export const initUserToken = function ({ $store, $cookie, $constants }) {
  // TODO: call init app version
  initAppVersion({
    $store,
    $cookie,
    $constants
  })

  if ($store.getters.token) return $store.getters.token
  const tokenCookie = $cookie.get($constants.COOKIE_NAME__TOKEN)
  if (tokenCookie) {
    $store.dispatch('setToken', tokenCookie)
    return tokenCookie
  }
  return null
}
export const initUserProfile = function ({ $store, $constants, $sentry }) {
  return new Promise(function (resolve) {
    if ($store.getters.userProfile) {
      resolve($store.getters.userProfile)
    } else {
      const profileStorage = getItemStorage($constants.LOCAL_STORAGE_KEY__USER)
      if (profileStorage) {
        const profileObj = JSON.parse(profileStorage)
        $store.dispatch('setUserProfile', profileObj).then(() => {
          resolve($store.getters.userProfile)
        })
        $sentry &&
          $sentry.appSetUserContext({
            id: profileObj._id
          })
      } else {
        resolve(null)
      }
    }
  })
}

export function getServiceDetail(services, serviceId) {
  let service = find(services, {
    _id: serviceId
  })
  return service
}

export function cloneDeepArray(input) {
  if (typeof input === 'object') {
    return cloneDeep(input)
  }

  let result = []
  input.forEach((point) => {
    result.push(cloneDeep(point))
  })
  return result
}

export function setAsLocalizedValue(data, $store) {
  if (data) {
    const locale = $store.getters.locale
    const suffix = $store.getters.languageIds[locale].toLowerCase()
    for (let key in data) {
      if (isObject(data[key])) {
        if (isArray(data[key])) {
          data[key].forEach((item) => {
            if (isObject(item) && !isArray(item)) {
              setAsLocalizedValue(item, $store)
            }
          })
        } else {
          setAsLocalizedValue(data[key], $store)
        }
      } else {
        const origin = key.replace(`_${suffix}`, '')
        const localizedValue = data[''.concat(key, '_', suffix)]
        data[origin] = key.indexOf(suffix) > -1 ? data[key] : localizedValue || data[origin]

        // if (localizedValue) {
        // 	data[key] = localizedValue
        // }
      }
    }
  }
}

var sanitize = require('sanitize-filename')
export const friendlyUrl = function (str, max) {
  if (typeof str !== 'string') {
    return ''
  }

  str = str.replace(/(á|à|ả|ã|ạ|ă|ắ|ằ|ẳ|ẵ|ặ|â|ấ|ầ|ẩ|ẫ|ậ)/g, 'a')
  str = str.replace(/(A|À|Ả|Ã|Ạ|Ă|Ắ|Ằ|Ẳ|Ẵ|Ặ|Â|Ấ|Ầ|Ẩ|Ẫ|Ậ)/g, 'A')
  str = str.replace(/đ/g, 'd')
  str = str.replace(/Đ/g, 'D')
  str = str.replace(/(é|è|ẻ|ẽ|ẹ|ê|ế|ề|ể|ễ|ệ)/g, 'e')
  str = str.replace(/(É|È|Ẻ|Ẽ|Ẹ|Ê|Ế|Ề|Ể|Ễ|Ệ)/g, 'E')
  str = str.replace(/(í|ì|ỉ|ĩ|ị)/g, 'i')
  str = str.replace(/(Í|Ì|Ỉ|Ĩ|Ị)/g, 'I')
  str = str.replace(/(ó|ò|ỏ|õ|ọ|ô|ố|ồ|ổ|ỗ|ộ|ơ|ớ|ờ|ở|ỡ|ợ)/g, 'o')
  str = str.replace(/(Ó|Ò|Ỏ|Õ|Ọ|Ô|Ố|Ồ|Ổ|Ỗ|Ộ|Ơ|Ớ|Ờ|Ở|Ỡ|Ợ)/g, 'O')
  str = str.replace(/(ú|ù|ủ|ũ|ụ|ư|ứ|ừ|ử|ữ|ự)/g, 'u')
  str = str.replace(/(Ú|Ù|Ủ|Ũ|Ụ|Ư|Ứ|Ừ|Ử|Ữ|Ự)/g, 'U')
  str = str.replace(/(ý|ỳ|ỷ|ỹ|ỵ)/g, 'y')
  str = str.replace(/(Ý|Ỳ|Ỷ|Ỹ|Ỵ)/g, 'Y')

  str = str.replace(/[^a-zA-Z0-9_-]/g, '-')

  while (str.length > 0 && /--/g.test(str)) {
    str = str.replace(/--/g, '-')
  }

  if (str) {
    str = sanitize(str)
    str = str.toLowerCase()

    const limit = max || 32
    return str.substring(0, limit)
  }

  return ''
}

export const formatPhoneNumber = (countryCode, phoneNumber) => {
  if (countryCode === undefined) countryCode = constants.DEFAULT_COUNTRY_ID
  const mobileCountryCode = getCallingCode(countryCode)
  if (phoneNumber) {
    const userPhone = phoneNumber.substring(mobileCountryCode.length, phoneNumber.length)
    const result = formatPhone(
      {
        country: countryCode,
        phone: userPhone
      },
      'International'
    )
    return result
  }
}

export const parseNumberPhoneVietnamese = (phoneNumber) => {
  let result = phoneNumber.replace(/[^0-9]/g, '')
  const countryCode = '84'
  if (result.startsWith('84')) {
    result = result.slice(2)
  } else if (result.startsWith('0')) {
    result = result.slice(1)
  }

  result = countryCode.concat(result)
  return result
}

export const getMultipointsStaticMapUrl = (path, size) => {
  let mapSize = size ? size : '390x240'
  let result =
    'https://maps.googleapis.com/maps/api/staticmap?size=' +
    mapSize +
    '&scale=2&key=' +
    process.env.VUE_APP_GOOGLE_MAP_API_KEY

  var str = ''
  path.forEach(function (point, point_index) {
    var color = point_index == 0 ? 'green' : 'red'
    str += '&markers=size:mid|color:' + color + '|' + point.lat + ',' + point.lng
  })

  result += str
  return result
}

export const getMultipointsStaticMapUrlFromAhaMap = (path, size) => {
  const mapSize = size ? size : '390x240'

  const pickupMarker = `https://cdn-ahamoveimg.ahamove.com/map/pickup-marker-focus.png`
  const dropOffMarker = `https://cdn-ahamoveimg.ahamove.com/map/path-marker-focus.png`

  let markers = []
  path.forEach(function (point, point_index) {
    let markerIcon = point_index === 0 ? pickupMarker : dropOffMarker
    markers.push(`marker=${point.lng},${point.lat}|${markerIcon}|scale:1`)
  })

  return (
    process.env.VUE_APP_AHAMOVE_MAP_API +
    `/styles/basic/static/auto/${mapSize}.png?${markers.join('&')}`
  )
}

export const getMultipointsStaticMapUrlFromMapbox = (path) => {
  let markers = path.map((point) => `${point.lng},${point.lat}`)
  let baseUri = process.env.VUE_APP_ONWHEEL_UTILS_URL + '/v1/mass/static-map'
  baseUri += `?rid=${encodeURIComponent(encodeRequestId(generateRequestId()))}`
  baseUri += `&markers=${encodeURIComponent(markers.join('|'))}`
  return baseUri
}

export const getLanguageId = ($store) => {
  const locale = $store.getters.locale
  return $store.state.languageIds[locale] || $store.state.languageIds.vi
}

export const detectBrowser = () => {
  let ua = navigator.userAgent,
    tem,
    M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || []
    return 'IE ' + (tem[1] || '')
  }
  if (M[1] === 'Chrome') {
    tem = ua.match(/\b(OPR|Edge)\/(\d+)/)
    if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera')
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?']
  if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1])
  return {
    name: M[0],
    version: M[1]
  }
}

// Validate phone number is number
// export const validatePhoneNumber = (value) => {
// 	if (!value || value === 0 || value === '0') return true
//
// 	var cleanedValue = typeof value === 'string' ? value.trim() : value.toString()
// 	const pattern = /^\d+$/
// 	const isNumber = pattern.test(cleanedValue)
// 	if (!isNumber) {
// 		return i18n.t('dropoff_address.form.invalid_mobile')
// 	}
// 	return true
// }

// Validate phone number by parsePhoneNumberFromString from libphonenumber-js
// export const validatePhoneNumber = (value) => {
// 	if (value) {
// 		var cleanedValue = typeof value === 'string' ? value.trim() : value.toString()
// 		const pattern = /^\d+$/
// 		const isNumber = pattern.test(cleanedValue)
// 		if (isNumber) {
// 			const phoneNumber = parsePhoneNumberFromString(cleanedValue, 'VN')
//
// 			// if (phoneNumber && phoneNumber.isPossible()) {
// 			if (phoneNumber && phoneNumber.isValid()) {
// 				return true
// 			}
// 		}
// 	}
// 	else return true
//
// 	return i18n.t('dropoff_address.form.invalid_mobile')
// }

// Validate phone number length 5 - 15
// format .eg:
// - 0972000000
// - +84972000000
export const validatePhoneNumber = (value) => {
  if (value) {
    var cleanedValue = typeof value === 'string' ? value.trim() : value.toString()
    const pattern = /^\+\d+$|^\d+$/
    const isNumber = pattern.test(cleanedValue)
    if (
      isNumber &&
      ((cleanedValue.charAt(0) === '+' && cleanedValue.length > 11 && cleanedValue.length < 14) ||
        (cleanedValue.charAt(0) !== '+' && cleanedValue.length > 9 && cleanedValue.length < 12))
    ) {
      return true
    }
  } else return true

  return i18n.t('invalid_mobile')
}

export const validatePhoneNumberForLogin = (value) => {
  if (value) {
    var cleanedValue = typeof value === 'string' ? value.trim() : value.toString()
    const pattern = /^\+\d+$|^\d+$/
    const isNumber = pattern.test(cleanedValue)
    if (
      isNumber &&
      ((cleanedValue.charAt(0) === '+' && cleanedValue.length > 11 && cleanedValue.length < 14) ||
        (cleanedValue.charAt(0) !== '+' && cleanedValue.length > 8 && cleanedValue.length < 12))
    ) {
      return true
    }
  } else return true

  return i18n.t('invalid_mobile')
}

const PointFields = [
  'lat',
  'lng',
  'address',
  'short_address',
  'formatted_address',
  'types',
  'name',
  'mobile',
  'cod',
  'tracking_number',
  'remarks',
  'apt_number',
  'building',
  'level',

  'require_pop',
  'pop_type',

  'require_pod',
  'pod_type',

  'types',
  'adr_source',
  'fb_conversation_id',
  'address_code',
  'weight',
  'items',
  'item_description',
  'item_descriptions',
  'item_value',
  'shipping_type',
  'partner'
]

export const cleanPathsData = (paths, pickCustomFields) => {
  let cleanedPaths = []

  if (paths && paths.length > 0) {
    const fields = pickCustomFields || PointFields

    paths.forEach((point, index) => {
      const pickObject = pick(point, fields)
      for (var key in pickObject) {
        // remove empty fields
        // remove cod field in pickup point
        if (
          pickObject[key] === '' ||
          (index === 0 && key === 'cod') ||
          (key === 'item_descriptions' && !pickObject[key])
        ) {
          delete pickObject[key]
        }
      }
      cleanedPaths.push(pickObject)
    })
  } else {
    console.error('Cannot clean empty paths')
  }

  return cleanedPaths
}

// Calc fee from origin data - rest api
export const legacy_getOrderFeeDetail = (estimatedFee) => {
  const {
    total_fee,
    discount,
    distance_fee,
    total_pay,
    partner_fee,
    partner_discount,
    partner_distance_fee,
    vat_fee
  } = estimatedFee
  // const isPartner = user && user.partner && true

  let serviceFee,
    subTotal,
    finalTotalPay,
    orderDiscount,
    feeAfterDiscount,
    payByCash,
    totalFee,
    surcharge

  if (!estimatedFee.payment_method && total_pay === 0) {
    // TODO remove when task WEB-381 is stabled/ data is stabled (cause old data of partner not have payment_menthod)
    estimatedFee.payment_method = constants.PAYMENT_METHODS.BALANCE
  }

  if (partner_fee && partner_distance_fee) {
    // For service has configs: partner_distance_fee/partner_fee
    serviceFee = partner_distance_fee
    totalFee = partner_fee
    subTotal = partner_fee
    orderDiscount = partner_discount || 0
    finalTotalPay = partner_fee - Math.abs(orderDiscount || 0)
    feeAfterDiscount = partner_fee - Math.abs(orderDiscount || 0)
    const partner_pay =
      partner_fee -
      (estimatedFee.user_main_account || 0) -
      (estimatedFee.user_bonus_account || 0) -
      orderDiscount
    payByCash = partner_pay || 0
    surcharge = estimatedFee.partner_surcharge
  } else {
    serviceFee = distance_fee
    totalFee = total_fee
    subTotal = total_fee
    orderDiscount = discount || 0
    finalTotalPay = total_fee - Math.abs(orderDiscount || 0)
    feeAfterDiscount = total_fee - Math.abs(discount || 0)
    payByCash = total_pay || 0
    surcharge = estimatedFee.surcharge
  }
  return {
    service_fee: serviceFee,
    discount: orderDiscount,
    sub_total: subTotal + (vat_fee || 0), // sub_total is total fee includes total fee (distance, COD, stop_point, request fee) + item Fee
    final_total_pay: finalTotalPay + (vat_fee || 0), // = sub_total - discount
    fee_after_discount: feeAfterDiscount,
    pay_by_cash: payByCash, // total money that user needs to pay by cash (may include order total fee, item fee)
    pay_by_balance: getPayByWalletValue(estimatedFee), // total money that user needs to pay by balance or pay by E-Wallet
    total_fee: totalFee, //
    surcharge: surcharge || 0,
    // TODO remove when task WEB-381 is stabled/ data is stabled (cause old data of partner not have payment_menthod)
    payment_method: estimatedFee.payment_method
  }
}

// Map fee from data - api
export const getOrderFeeDetail = (estimatedFee = {}) => {
  const {
    distance_price,
    voucher_discount,
    special_request_price,
    stoppoint_price,
    subtotal_price,
    total_price,
    vat,
    total_pay,
    surge_rate
  } = estimatedFee

  if (!estimatedFee.payment_method && total_pay === 0) {
    // TODO remove when task WEB-381 is stabled/ data is stabled (cause old data of partner not have payment_menthod)
    estimatedFee.payment_method = constants.PAYMENT_METHODS.BALANCE
  }

  return {
    service_fee: distance_price || 0,
    distance_fee: distance_price || 0, // Rewrite field
    total_fee: subtotal_price || 0,
    discount: voucher_discount || 0,
    // Additional request fee
    request_fee: special_request_price || 0, // Map field from api to local field
    stop_fee: stoppoint_price || 0, // Map field from api to local field
    // sub_total/total_fee is total fee includes total fee (service_fee, stop_fee, request fee)
    sub_total: subtotal_price || 0, // Map field from api to local field
    // Equal sub_total - discount
    final_total_pay: total_price || 0, // Map field from api to local field
    // Total money that user need to pay by cash( equal 0 if payment_method = BALANCE/MOMO)
    pay_by_cash: total_pay || 0,
    // total money that user needs to pay by balance or pay by E-Wallet
    pay_by_balance: getPayByWalletValue(estimatedFee),
    surcharge: surge_rate || 0,
    vat_fee: vat || 0,
    // TODO remove when task WEB-381 is stabled/ data is stabled (cause old data of partner not have payment_menthod)
    payment_method: estimatedFee.payment_method
  }
}

const getPayByWalletValue = (estimatedFee) => {
  let result = 0
  switch (estimatedFee.payment_method) {
    case constants.PAYMENT_METHODS.MOMO:
      result = estimatedFee.online_pay
      break
    default:
      result = (estimatedFee.user_bonus_account || 0) + (estimatedFee.user_main_account || 0)
  }
  return result
}
export const formatTimeCalendar = (time, locale, defaultFormat) => {
  if (time) {
    const formatTime = defaultFormat ? defaultFormat : 'DD-MM-Y, HH:mm'
    if (locale) {
      moment.locale(locale)
    }
    return moment.unix(time).calendar(null, {
      lastDay() {
        return formatTime
      },
      lastWeek() {
        return formatTime
      },
      nextDay() {
        return formatTime
      },
      nextWeek() {
        return formatTime
      },
      sameElse: formatTime
    })
  } else return ''
}

export const getMaxStopPointByService = (service, profile, isRoTool = false) => {
  const serviceMaxStopPoints = (service && service.max_stop_points) || 10
  const profileMaxStopPoint = (profile && profile.max_stop_points) || 0
  const maxStopPoints =
    serviceMaxStopPoints > 1
      ? Math.max(profileMaxStopPoint, serviceMaxStopPoints)
      : serviceMaxStopPoints
  if (service.delivery_type === constants.DELIVERY_TYPE.TRUCK && !isRoTool) {
    return 10
  }
  return maxStopPoints
}

export const canLoadDataFromAPI = (profile) => {
  return profile && profile.partner && profile.verified && isROExpertUser(profile)
}
export const isROExpertUser = (profile) => {
  return profile && profile.tags && profile.tags.indexOf(constants.RO_EXPERT) > -1
}

export const setOrderTime = (params, orderTime, service, setOrderTimeForPickup) => {
  if (service.advance_broadcast_distance == 0) {
    params.order_time = 0
  } else {
    const diff = orderTime ? moment.unix(orderTime) - moment() : 0
    if (diff >= constants.MIN_IDLE_UNTIL_MILLISECONDS) {
      if (!setOrderTimeForPickup) params.idle_until = orderTime
      params.order_time = orderTime
    } else {
      params.order_time = 0
    }
  }
}

export const estimateFeeParamsCreator = ({
  service,
  path,
  requests,
  images,
  promoCode,
  remarks,
  token,
  payment_method,
  orderTime,
  setOrderTimeAsPickupTime,
  maxCOD
}) => {
  let params = {}

  if (token) {
    params.token = token
  }

  // path
  let validPath = filter(path, (item) => {
    return !item.disabled && item.is_valid
  })
  validPath = cleanPathsData(validPath)
  delete validPath[0].cod

  if (!maxCOD) {
    validPath.forEach((p) => {
      delete p.cod
    })
  } else {
    validPath.forEach((p) => {
      if (p.cod) {
        p.cod = typeof p.cod === 'string' ? parseFloat(p.cod) : p.cod
        if (isNaN(p.cod) || p.cod <= 0 || p.cod > maxCOD) {
          delete p.cod
        }
      } else if (p.cod === '') {
        delete p.cod
      }
    })
  }
  // TODO support SAAS service
  if (service.type && service.partner && service.notify_package_return && validPath[1]) {
    validPath[1].require_pod = true
  }

  params.path = JSON.stringify(validPath)

  params.service_id = service._id

  // special requests
  let selectedRequests = []
  if (requests) {
    requests.forEach((rq) => {
      const isValid = find(service.requests, {
        _id: rq._id
      })
      if (isValid) {
        selectedRequests.push(rq)
      }
    })
    params.requests = JSON.stringify(selectedRequests)
  }

  // images
  if (images && images.length > 0) {
    params.images = JSON.stringify(images)
  }

  // promo_code
  if (promoCode) params.promo_code = promoCode

  // remarks
  if (remarks) params.remarks = remarks

  // payment method
  if (payment_method) {
    // params.payment_method = payment_method
    const servicePaymentMethods = service.payment_method || []
    if (servicePaymentMethods.length > 0) {
      const isEnablePaymentMethod = servicePaymentMethods.find(
        (item) => item.payment === payment_method.toLowerCase()
      )
      if (isEnablePaymentMethod) {
        params.payment_method = payment_method
      } else {
        params.payment_method = servicePaymentMethods[0].payment.toUpperCase()
      }
    } else {
      params.payment_method = payment_method
    }
  }

  // order_time
  setOrderTime(params, orderTime, service, setOrderTimeAsPickupTime)

  return params
}

export const payByEWallet = (paymentMethod) => {
  const eWallets = [
    constants.PAYMENT_METHODS.MOMO,
    constants.PAYMENT_METHODS.ZALOPAY,
    // constants.PAYMENT_METHODS.SMARTPAY,
    constants.PAYMENT_METHODS.VIETTELPAY,
    constants.PAYMENT_METHODS.SHOPEEPAY,
    constants.PAYMENT_METHODS.CARD
  ]
  return eWallets.indexOf(paymentMethod) > -1
}

export const getPayByWalletText = (paymentMethod, payByBalance) => {
  if (paymentMethod) {
    return i18n.t('payment_method.text_fee.' + paymentMethod)
  } else if (payByBalance > 0) {
    return i18n.t('payment_method.text_fee.BALANCE')
  } else return i18n.t('payment_method.text_fee.CASH')
}

export const getPaymentMethodAssets = (paymentMethod) => {
  if (typeof paymentMethod !== 'string' || paymentMethod.trim() === '') {
    return {
      name: i18n.t('payment_method.by_cash'),
      icon: '/assets/images/icons/Cash.svg'
    }
  }
  const upperCaseMethod = paymentMethod.toUpperCase()

  switch (upperCaseMethod) {
    case constants.PAYMENT_METHODS.CASH:
      return {
        name: i18n.t('payment_method.by_cash'),
        icon: '/assets/images/icons/Cash.svg'
      }
    case constants.PAYMENT_METHODS.CASH_BY_RECIPIENT:
      return {
        name: i18n.t('payment_method.by_recipient'),
        icon: '/assets/images/icons/CashByRecipient.svg'
      }
    case constants.PAYMENT_METHODS.BALANCE:
      return {
        name: i18n.t('payment_method.by_balance'),
        icon: '/assets/images/icons/PersonalBalance.svg'
      }
    case constants.PAYMENT_METHODS.BUSINESS_ACCOUNT:
      return {
        name: i18n.t('payment_method.by_business_account'),
        icon: '/assets/images/icons/BusinessAccount.svg'
      }
    case constants.PAYMENT_METHODS.MOMO:
      return {
        name: i18n.t('payment_method.by_momo'),
        icon: '/assets/images/icons/Momo.svg?ver=1'
      }
    case constants.PAYMENT_METHODS.ZALOPAY:
      return {
        name: i18n.t('payment_method.by_zalopay'),
        icon: '/assets/images/icons/ZaloPayIcon.svg?ver=1'
      }
    case constants.PAYMENT_METHODS.SMARTPAY:
      return {
        name: i18n.t('payment_method.by_smartpay'),
        icon: '/assets/images/logo_SmartPay.png?ver=1'
      }
    case constants.PAYMENT_METHODS.VNPAY:
      return {
        name: i18n.t('payment_method.by_vnpay'),
        icon: 'https://ahamoveimg.s3-ap-southeast-1.amazonaws.com/Logo+Partners/Logo_VNPAY.png?ver=1'
      }
    case constants.PAYMENT_METHODS.VIETTELPAY:
      return {
        name: i18n.t('payment_method.by_viettelpay'),
        icon: 'https://ahamoveimg.s3-ap-southeast-1.amazonaws.com/Logo+Partners/Logo_ViettelPay.png?ver=1'
      }
    case constants.PAYMENT_METHODS.SHOPEEPAY:
      return {
        name: i18n.t('payment_method.by_shopeepay'),
        icon: 'https://ahamoveimg.s3.ap-southeast-1.amazonaws.com/Logo+Partners/Logo_ShopeePay.png?ver=1'
      }
    case constants.PAYMENT_METHODS.BANKTRANSFER:
      return {
        name: i18n.t('payment_method.by_bank_transfer'),
        icon: 'https://ahamoveimg.s3.ap-southeast-1.amazonaws.com/Logo/Bank-transfer-icon.jpg?ver=1'
      }
    default:
      return {
        name: i18n.t('payment_method.by_cash'),
        icon: '/assets/images/icons/Cash.svg'
      }
  }
}

export async function checkingUserAuthPerPage(page, profile) {
  if (profile) {
    return true
    // if (page === constants.PAGE.PARTNER) {
    //   console.log('partner page', page)
    //   if (!profile.partner || profile.partner === "ahamove") {
    //     return false;
    //   }

    //   if (
    //       !profile.tags
    //       || (
    //         !profile.tags.includes(constants.PARTNER_TAG)
    //         && profile.saas !== true
    //       )
    //   ) {
    //     if (profile.parent_id) {
    //       const url = `${global.$_VUE_APP_REST_API}/${constants.API.rest_api.check_partner_user}?user_id=${profile.parent_id}`;
    //       const response = await fetch(url);
    //       const parent = await response.json();

    //       if (response.status === 200 && parent.partner === profile.partner) {
    //         return true;
    //       } else return false;
    //     } else return false;
    //   }
    // }
  } else return false

  // return true;
}

export function removeUnicode(str = '') {
  str = str.toLowerCase()
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i')
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
  str = str.replace(/đ/g, 'd')

  return str
}

const COLOR_TEMPLATES = [
  '#2F80ED',
  '#E12B38',
  '#FCC133',
  '#FB8122',
  '#3EB650',
  '#9C45EB',
  '#292930',
  '#4D724D',
  '#AD4328',
  '#C19434',
  '#25459B',
  '#D97441',
  '#7D3780',
  '#B5267B',
  '#3CBCC3',
  '#DDEDF4',
  '#F4ABAA',
  '#040F3D'
]

export function generateColor(index) {
  if (!isNaN(index)) {
    const totalColorsIndex = COLOR_TEMPLATES.length - 1
    if (index <= totalColorsIndex) {
      return COLOR_TEMPLATES[index]
    } else {
      return COLOR_TEMPLATES[(index % totalColorsIndex) - 1]
    }
  } else return '#3EB650'
}

export const generateDensityRange = (max) => {
  const number = 8
  if (max > number) {
    let range = []

    const step = Math.floor(max / number)

    for (let i = 0; i < number; i++) {
      range.push(step * i)
    }

    return range
  } else {
    return [0, 1, 2, 3, 4, 5, 6, 7, 8]
  }
}

export function getCookie(key) {
  const value = VueCookie.get(key)
  return value
}

export function setCookie(cname, cvalue, exdays) {
  let params = [cname, cvalue]

  if (exdays)
    params.push({
      expires: exdays
    })

  VueCookie.set.apply(void 0, params)
}

// kolodny/weak-key
// Get a unique key for an object
var WeakMap = global.WeakMap
var map = new WeakMap()
var index = 0

export function weakKey(obj) {
  var key = map.get(obj)

  if (!key) {
    key = 'weak-key-' + index++
    map.set(obj, key)
  }

  return key
}

// https://stackoverflow.com/a/45411081
export function scrollParentToChild(parent, child) {
  // Where is the parent on page
  const parentRect = parent.getBoundingClientRect()
  // What can you see?
  // const parentViewableArea = {
  //   height: parent.clientHeight,
  //   width: parent.clientWidth
  // };

  // Where is the child
  const childRect = child.getBoundingClientRect()

  // Distance of child to parent on view port
  const offsetToTop = childRect.top - parentRect.top

  // Is the child viewable?
  // var isViewable = (childRect.top >= parentRect.top) && (childRect.top <= parentRect.top + parentViewableArea.height);

  // scroll by offset relative to parent
  if (childRect.top != parentRect.top) {
    parent.scrollTop = offsetToTop + parent.scrollTop
  }
}

export function getOpeningHours(input) {
  if (!input) return {}
  // sameple input = '1-15'
  const hours = input.split('-')

  if (hours.length === 2) {
    return {
      from: moment().hour(hours[0]).minute(0).format('H:mm A'),
      to: moment().hour(hours[1]).minute(0).format('H:mm A')
    }
  } else return {}
}

export function getStaticMapUrl(config = {}, source = 'google') {
  if (config.lat && config.lng) {
    const mapSize = config.size || '900x500'
    const scale = config.scale || 2
    const GOOGLE_KEY = process.env.VUE_APP_GOOGLE_MAP_API_KEY

    if (source === 'ahamove') {
      const zoom = config.zoom || 15
      const center = `${config.lng},${config.lat}`
      // return `http://localhost:10001/styles/basic/static/${center},${zoom}/${mapSize}.jpg`
      return `${process.env.VUE_APP_AHAMOVE_MAP_API}/styles/basic/static/${center},${zoom}/${mapSize}.jpg`
    } else if (source === 'google') {
      const center = `${config.lat},${config.lng}`
      const zoom = config.zoom || 13
      const maptype = config.maptype || 'terrain'
      const style =
        config.style ||
        '&style=feature:administrative.land_parcel%7Celement:labelsvisibility:off&style=feature:poi%7Celement:labels.textvisibility:off&style=feature:poi.business%7Cvisibility:off&style=feature:road%7Celement:labels.iconvisibility:off&style=feature:road.local%7Celement:labelsvisibility:off&style=feature:transit%7Cvisibility:off'

      return `https://maps.googleapis.com/maps/api/staticmap?center=${center}${style}&size=${mapSize}&zoom=${zoom}&scale=${scale}&maptype=${maptype}&key=${GOOGLE_KEY}`
    }
  }

  return ''
}

// Refs: https://davidwalsh.name/detect-scrollbar-width
export function detectScrollbarWith() {
  // Detect scroll bar vertical with

  // Create the measurement node
  const scrollDiv = document.createElement('div')
  // add scrollbar-measure css to document
  scrollDiv.className = 'scrollbar-measure'
  document.body.appendChild(scrollDiv)

  // Get the scrollbar width
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth

  // Delete the DIV
  document.body.removeChild(scrollDiv)

  return scrollbarWidth
}
export const filterWithRemoveUnicode = (items = [], itemKeys = [], value) => {
  if (itemKeys.length > 0) {
    if (value) {
      const _value = removeUnicode(value)

      return items.filter((i) => {
        return itemKeys.some((itemKey) => {
          if (i && i[itemKey]) {
            const itemValue = removeUnicode(i[itemKey])

            return itemValue.includes(_value)
          } else return false
        })
      })
    } else return items
  } else return []
}

export const getFCMSubscriptionToken = () => {
  if (window.app && window.app.subscription) return window.app.subscription

  const fcmTokenLocalStorage = getItemStorage(constants.LOCAL_STORAGE_KEY__FCM_TOKEN)
  if (fcmTokenLocalStorage) return fcmTokenLocalStorage

  const fcmSubscriptionDetailFromMoengage = getCookie('SUBSCRIPTION_DETAILS')
  if (fcmSubscriptionDetailFromMoengage) {
    try {
      const decoded = decodeURIComponent(fcmSubscriptionDetailFromMoengage)
      const fcmSubscriptionObj = JSON.parse(decoded)
      if (fcmSubscriptionObj && fcmSubscriptionObj.token) {
        return fcmSubscriptionObj.token
      }
    } catch (e) {
      console.log('cannot decode obj')
    }
  }

  return ''
}

export function capitalize(str = '') {
  str = str
    .trim()
    .toLowerCase()
    .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
  return str
}

export function formatPath(path) {
  if (!path.address) {
    return path
  }

  const first_label = path.address.split(',')[0]
  const start = first_label.length
  const length = path.address.length

  return {
    ...path,
    second_label:
      path.address.substring(start, length).trim().replace(',', '') || path.short_address,
    first_label: first_label
  }
}

export function generateRandomString(length = 6) {
  return Math.random().toString(20).substr(2, length)
}

/**
 * XSS prevent by escape <>"')(;/# from string
 * @param {string} str
 * @returns {string} String without characters: &<>
 */
export function stringEscape(str) {
  const tagsToReplace = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;'
  }
  return String(str).replace(/[&<>]/g, function (tag) {
    return tagsToReplace[tag] || ''
  })
}

export function getRSAEncryption(str) {
  try {
    const isProd = process.env.VUE_APP_ENV === 'production'
    const k = require(`./../../public/assets/documents/${
      isProd ? 'rsa.prod.txt' : 'rsa.txt'
    }`).default
    const encrypt = new JSEncrypt()
    encrypt.setPublicKey(k)
    return encrypt.encrypt(str)
  } catch (error) {
    console.log(error)
    return ''
  }
}

export function getFromCookieSafe(dataFromCookie, path, defaultValue) {
  try {
    const result = get(JSON.parse(dataFromCookie || '{}'), path, defaultValue)

    return result
  } catch (e) {
    return defaultValue
  }
}

export function formatCurrency(value) {
  if (!value) return

  return `${constants.CURRENCY_CONFIG.prefix}${value.toLocaleString()}`
}
